<template>
  <div class="dept-performance-addon">
    <div class="addon-head">
      <div>
        <span>HRM_DEPT_PERFORMANCE_CORRELATION</span>
        <h2>部门绩效相关性分析</h2>
        <p>将部门人员规模、出勤健康度、请假天数、薪资扣减和人均成本聚合为风险象限与绩效相关性看板。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门绩效相关矩阵</div>
          <el-table :data="overview.deptCorrelationMatrix" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="staff_count" label="人数" width="70" />
            <el-table-column prop="attendance_score" label="出勤分" width="90" />
            <el-table-column prop="leave_days_per_staff" label="人均请假" width="90" />
            <el-table-column prop="deduct_ratio" label="扣减比%" width="90" />
            <el-table-column prop="performance_score" label="综合分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">分析权重</div>
          <div class="driver-list">
            <div v-for="item in overview.driverWeights" :key="item.driver_name" class="driver-item">
              <span>{{ item.driver_name }}</span>
              <strong>{{ item.weight_value }}%</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">高风险部门</div>
          <el-table :data="overview.highRiskDeptList" size="small" v-loading="loading" empty-text="暂无高风险部门">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="performance_score" label="综合分" width="90" />
            <el-table-column prop="abnormal_rate" label="异常率%" width="90" />
            <el-table-column prop="deduct_ratio" label="扣减比%" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">高效部门</div>
          <el-table :data="overview.highEfficiencyDeptList" size="small" v-loading="loading" empty-text="暂无高效部门">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="performance_score" label="综合分" width="90" />
            <el-table-column prop="attendance_score" label="出勤分" width="90" />
            <el-table-column prop="avg_total_salary" label="平均薪资" width="100" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">成本与出勤象限</div>
          <div class="quadrant-list">
            <div v-for="item in overview.costAttendanceQuadrant" :key="item.dept_name" class="quadrant-item">
              <span>{{ item.dept_name }}</span>
              <strong>{{ item.quadrant_name }}</strong>
              <em>出勤 {{ item.attendance_score }} / 成本 {{ item.avg_total_salary }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">月度趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.attendance_score }}</strong>
              <em>薪资 {{ item.salary_total }} / 扣减 {{ item.total_deduct }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getDeptPerformanceCorrelationOverview } from '../api/deptPerformanceCorrelation'

export default {
  name: 'DeptPerformanceCorrelationOverview',
  data () {
    return {
      loading: false,
      overview: {
        deptCount: 0,
        activeDeptCount: 0,
        attendanceRecordCount: 0,
        salaryRecordCount: 0,
        deptCorrelationMatrix: [],
        highRiskDeptList: [],
        highEfficiencyDeptList: [],
        costAttendanceQuadrant: [],
        monthlyTrend: [],
        driverWeights: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '部门总数', value: this.overview.deptCount, meta: `${this.overview.activeDeptCount} 个有在职人员` },
        { label: '考勤样本', value: this.overview.attendanceRecordCount, meta: '纳入相关性分析', tone: 'blue' },
        { label: '薪资样本', value: this.overview.salaryRecordCount, meta: '计算成本与扣减', tone: 'orange' },
        { label: '风险部门', value: this.overview.highRiskDeptList.length, meta: '需管理复核', tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getDeptPerformanceCorrelationOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '部门绩效相关性分析加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.dept-performance-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.driver-list,
.quadrant-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.quadrant-list,
.trend-list {
  max-height: 360px;
  overflow: auto;
}

.driver-item,
.quadrant-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.quadrant-item,
.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.driver-item span,
.quadrant-item span,
.quadrant-item em,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.driver-item strong,
.quadrant-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
